import {
  type FragmentOf,
  type ResultOf,
  readFragment,
  type TadaDocumentNode,
} from "gql.tada";
import { ADS_ANALYTICS_FEATURE } from "@/app/[lang]/[workspaceId]/ads/constants";
import { SPECIAL_VIEW_IDS } from "../../sortable/sortable-types";
import {
  AdsAnalyticsProjectItemFragment,
  filterProjectsByType,
  IndexWorkerProjectItemFragment,
  OrganizationProjectsFragment,
  SearchAnalyticsProjectItemFragment,
  UserAnalyticsProjectItemFragment,
} from "./fragments";
import type { SidebarSearchSources } from "./sidebar-search-contract";
import { WorkspaceSidebarDataQuery } from "./workspace-sidebar-data-query";

export type WorkspaceSidebarData = ResultOf<typeof WorkspaceSidebarDataQuery>;

type Dashboard = NonNullable<
  Extract<WorkspaceSidebarData["node"], { __typename?: "Organization" }>
>["dashboards"][number];

type SearchAnalyticsSession = NonNullable<
  WorkspaceSidebarData["unfinishedSearchAnalyticsOnboardingSessions"]
>[number] & { projectID: string };

type UserAnalyticsSession = NonNullable<
  WorkspaceSidebarData["unfinishedUserAnalyticsOnboardingSessions"]
>[number] & { projectID: string };

/** Normalized data consumed by the five sidebar group presenters. */
export type WorkspaceSidebarModel = {
  dashboards: {
    items: Dashboard[];
    order: readonly string[] | null;
  };
  iw: {
    projects: FragmentOf<typeof IndexWorkerProjectItemFragment>[];
  };
  sa: {
    projects: FragmentOf<typeof SearchAnalyticsProjectItemFragment>[];
    unfinished: SearchAnalyticsSession[];
    completed: SearchAnalyticsSession[];
  };
  ua: {
    projects: FragmentOf<typeof UserAnalyticsProjectItemFragment>[];
    unfinished: UserAnalyticsSession[];
    completed: UserAnalyticsSession[];
  };
  ads: {
    enabled: boolean;
    projects: FragmentOf<typeof AdsAnalyticsProjectItemFragment>[];
  };
  workspaceUsedColors: string[];
  searchSources: SidebarSearchSources;
};

function sortByOrder<T>(
  items: readonly T[],
  order: readonly string[] | null | undefined,
  getId: (item: T) => string,
): T[] {
  if (!order || order.length === 0) return [...items];

  const remaining = new Map(items.map((item) => [getId(item), item]));
  const sorted = order.flatMap((id) => {
    const item = remaining.get(id);
    if (!item) return [];
    remaining.delete(id);
    return [item];
  });

  return [...sorted, ...remaining.values()];
}

function sortFragmentByOrder<
  // biome-ignore lint/suspicious/noExplicitAny: gql.tada fragment documents carry varying result types.
  TDocument extends TadaDocumentNode<any, any, any>,
>(
  items: readonly FragmentOf<TDocument>[],
  order: readonly string[] | null | undefined,
  fragment: TDocument,
): FragmentOf<TDocument>[] {
  return sortByOrder(items, order, (item) => readFragment(fragment, item).id);
}

function sessionsWithProjectId<
  T extends { readonly projectID?: string | null },
>(sessions: readonly T[] | null | undefined): (T & { projectID: string })[] {
  return (sessions ?? []).filter(
    (session): session is T & { projectID: string } =>
      Boolean(session.projectID),
  );
}

/**
 * Converts the combined query result into one stable, presentation-ready
 * model so search and visible groups cannot disagree about order or entities.
 */
export function buildWorkspaceSidebarModel(
  data: WorkspaceSidebarData | null | undefined,
): WorkspaceSidebarModel {
  const organizationNode = data?.node;
  const organization =
    organizationNode?.__typename === "Organization"
      ? readFragment(OrganizationProjectsFragment, organizationNode)
      : null;
  const projects = organization?.projects ?? [];

  const iwProjects = sortFragmentByOrder(
    filterProjectsByType<typeof IndexWorkerProjectItemFragment>(
      projects,
      "IndexWorkerProject",
    ),
    organization?.projectOrderIW,
    IndexWorkerProjectItemFragment,
  );
  const saProjects = sortFragmentByOrder(
    filterProjectsByType<typeof SearchAnalyticsProjectItemFragment>(
      projects,
      "SearchAnalyticsProject",
    ),
    organization?.projectOrderSA,
    SearchAnalyticsProjectItemFragment,
  );
  const uaProjects = sortFragmentByOrder(
    filterProjectsByType<typeof UserAnalyticsProjectItemFragment>(
      projects,
      "UserAnalyticsProject",
    ),
    organization?.projectOrderUA,
    UserAnalyticsProjectItemFragment,
  );
  const adsProjects = sortFragmentByOrder(
    filterProjectsByType<typeof AdsAnalyticsProjectItemFragment>(
      projects,
      "AdsAnalyticsProject",
    ),
    organization?.projectOrderAA,
    AdsAnalyticsProjectItemFragment,
  );

  const unfinishedSa = sessionsWithProjectId(
    data?.unfinishedSearchAnalyticsOnboardingSessions,
  );
  const completedSa = sessionsWithProjectId(
    data?.completedSearchAnalyticsOnboardingSessions,
  );
  const unfinishedUa = sessionsWithProjectId(
    data?.unfinishedUserAnalyticsOnboardingSessions,
  );
  const completedUa = sessionsWithProjectId(
    data?.completedUserAnalyticsOnboardingSessions,
  );
  const saOnboardingProjectIds = new Set(
    unfinishedSa
      .filter((session) => session.status !== "APPLIED")
      .map((session) => session.projectID),
  );
  const uaOnboardingProjectIds = new Set(
    unfinishedUa
      .filter((session) => session.status !== "APPLIED")
      .map((session) => session.projectID),
  );

  const dashboardNode =
    organizationNode?.__typename === "Organization" ? organizationNode : null;
  const dashboardOrder = dashboardNode?.dashboardOrder ?? null;
  const dashboards = sortByOrder(
    dashboardNode?.dashboards ?? [],
    dashboardOrder,
    (dashboard) => dashboard.id,
  );
  const adsEnabled =
    organization?.enabledFeatures.includes(ADS_ANALYTICS_FEATURE) ?? false;
  const workspaceUsedColors = [
    ...new Set(
      projects
        .map((project) => ("color" in project ? project.color : undefined))
        .filter((color): color is string => Boolean(color)),
    ),
  ];

  const searchSources = {
    dashboards: {
      label: null,
      isVisible: true,
      projects: dashboards.map((dashboard) => ({
        id: dashboard.id,
        label: dashboard.name,
        items: [],
      })),
    },
    iw: {
      label: null,
      isVisible: true,
      projects: iwProjects.map((projectData) => {
        const project = readFragment(
          IndexWorkerProjectItemFragment,
          projectData,
        );
        return {
          id: project.id,
          label: project.name,
          items: (project.indexWorkers ?? []).map((item) => ({
            id: item.id,
            label: item.name,
          })),
        };
      }),
    },
    sa: {
      label: null,
      isVisible: true,
      projects: saProjects.map((projectData) => {
        const project = readFragment(
          SearchAnalyticsProjectItemFragment,
          projectData,
        );
        return {
          id: project.id,
          label: project.name,
          items: [
            ...(saOnboardingProjectIds.has(project.id)
              ? [{ id: SPECIAL_VIEW_IDS.INITIAL_ANALYSIS, label: null }]
              : []),
            ...(project.searchAnalytics?.bigQuery?.latestDataDate
              ? [{ id: SPECIAL_VIEW_IDS.ALL, label: null }]
              : []),
            ...(project.searchAnalytics?.views ?? []).map((view) => ({
              id: view.id,
              label: view.name,
            })),
          ],
        };
      }),
    },
    ua: {
      label: null,
      isVisible: true,
      projects: uaProjects.map((projectData) => {
        const project = readFragment(
          UserAnalyticsProjectItemFragment,
          projectData,
        );
        return {
          id: project.id,
          label: project.name,
          items: [
            ...(uaOnboardingProjectIds.has(project.id)
              ? [{ id: SPECIAL_VIEW_IDS.INITIAL_ANALYSIS, label: null }]
              : []),
            ...(project.userAnalytics?.bigQuery?.latestDataDate
              ? [
                  { id: SPECIAL_VIEW_IDS.ALL, label: null },
                  { id: SPECIAL_VIEW_IDS.SESSION_EXPLORER, label: null },
                ]
              : []),
            ...(project.userAnalytics?.views ?? []).map((view) => ({
              id: view.id,
              label: view.name,
            })),
          ],
        };
      }),
    },
    ads: adsEnabled
      ? {
          label: null,
          isVisible: true,
          projects: adsProjects.map((projectData) => {
            const project = readFragment(
              AdsAnalyticsProjectItemFragment,
              projectData,
            );
            return {
              id: project.id,
              label: project.name,
              items: [
                ...(project.adsAnalytics?.latestDataDate
                  ? [{ id: SPECIAL_VIEW_IDS.ALL, label: null }]
                  : []),
                ...(project.adsAnalytics?.views ?? []).map((view) => ({
                  id: view.id,
                  label: view.name,
                })),
              ],
            };
          }),
        }
      : { label: null, isVisible: false, projects: [] },
  } satisfies SidebarSearchSources;

  return {
    dashboards: { items: dashboards, order: dashboardOrder },
    iw: { projects: iwProjects },
    sa: {
      projects: saProjects,
      unfinished: unfinishedSa,
      completed: completedSa,
    },
    ua: {
      projects: uaProjects,
      unfinished: unfinishedUa,
      completed: completedUa,
    },
    ads: { enabled: adsEnabled, projects: adsProjects },
    workspaceUsedColors,
    searchSources,
  };
}
